import { Button } from "@/components/ui/button";
import Text from "@/components/Text";
import { getGrids, getGridsState } from "@/store/getGridsState";
import { useEffect, useState, type FC } from "react";
import { Link, useNavigate } from "react-router";
import { Calendar, Copy, Ellipsis, Pencil, Trash } from "lucide-react";
import Badge from "@/components/Badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  deleteGrid,
  deleteGridState,
  resetDeleteGridState,
} from "@/store/deleteGridState";
import { toast } from "sonner";
import { Skeleton } from "@/components/ui/skeleton";
import {
  duplicateGrid,
  duplicateGridState,
  resetduplicateGridState,
} from "@/store/duplicateGridState";
import DialogDeleteGrid from "./components/DialogDeleteGrid";
import { useTranslation } from "react-i18next";
import DialogUpdatePlanification from "./components/DialogUpdatePlanification";
import type { Grid } from "@/API";
import { currentSubscriptionState } from "@/store/currentSubscriptionState";
import Landing from "./components/Landing";

const GridBuilder: FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { data: getGridsData, isLoading: getGridsIsLoading } = getGridsState();
  const { data: deleteGridData, isLoading: deleteGridIsLoading } =
    deleteGridState();
  const { data: duplicateGridData, isLoading: duplicateGridIsLoading } =
    duplicateGridState();
  const { data: stripeSubscription } = currentSubscriptionState();

  const [gridToDelete, setGridToDelete] = useState<string | null>(null);
  const [isDialogDeleteGridOpen, setIsDialogDeleteGridOpen] = useState(false);
  const [isDialogUpdatePlanificationOpen, setIsDialogUpdatePlanificationOpen] =
    useState(false);
  const [gridToUpdate, setGridToUpdate] = useState<Grid | null>(null);

  useEffect(() => {
    getGrids();
  }, []);

  useEffect(() => {
    if (deleteGridIsLoading) {
      toast(t("deleting_grid"));
      setIsDialogDeleteGridOpen(false);
      setGridToDelete(null);
    }

    if (deleteGridData) {
      toast(t("deleted_grid"));
      resetDeleteGridState();
    }
  }, [deleteGridData, deleteGridIsLoading]);

  useEffect(() => {
    if (duplicateGridIsLoading) {
      toast(t("duplicating_grid"));
    }

    if (duplicateGridData) {
      toast(t("duplicated_grid"));
      resetduplicateGridState();
    }
  }, [duplicateGridData, duplicateGridIsLoading]);

  if (!stripeSubscription) {
    return <Landing isSubscribed={false} />;
  }

  if (getGridsIsLoading) {
    return (
      <div
        style={{
          height: "calc(100dvh - var(--height-header))",
        }}
        className="bg-[#F7F8F9] p-[40px] overflow-y-auto overflow-x-hidden flex flex-col"
      >
        <div className="flex justify-between">
          <div className="flex flex-col">
            <Skeleton className="mb-[8px] h-[40px] w-[200px]" />
            <Skeleton className="h-[20px] w-[500px]" />
          </div>
          <Skeleton className="h-[40px] w-[140px]" />
        </div>
        <div className="grid grid-cols-2 2xl:grid-cols-3 mt-[64px] gap-[16px]">
          <Skeleton className="w-[100%] h-[100px]" />
          <Skeleton className="w-[100%] h-[100px]" />
          <Skeleton className="w-[100%] h-[100px]" />
          <Skeleton className="w-[100%] h-[100px]" />
          <Skeleton className="w-[100%] h-[100px]" />
        </div>
      </div>
    );
  }

  if (getGridsData?.length === 0) return <Landing isSubscribed />;

  return (
    <>
      <DialogDeleteGrid
        open={isDialogDeleteGridOpen}
        onOpenChange={(open) => setIsDialogDeleteGridOpen(open)}
        onDelete={() => {
          if (gridToDelete) deleteGrid({ gridId: gridToDelete });
        }}
      />
      {gridToUpdate && (
        <DialogUpdatePlanification
          open={isDialogUpdatePlanificationOpen}
          onOpenChange={(open) => {
            if (!open) setGridToUpdate(null);
            setIsDialogUpdatePlanificationOpen(open);
          }}
          grid={gridToUpdate}
        />
      )}

      <div
        style={{
          height: "calc(100dvh - var(--height-header))",
        }}
        className="bg-[#F7F8F9] p-[40px] overflow-y-auto overflow-x-hidden flex flex-col"
      >
        <div className="flex justify-between">
          <div className="flex flex-col">
            <Text fontSize={32} fontWeight={600} className="mb-[8px]">
              {t("grid_builder")}
            </Text>
            <Text fontSize={16} fontWeight={400}>
              {t("grid_builder_description")}
            </Text>
          </div>
          <Link to="/grid-builder/create">
            <Button>{t("create_view")}</Button>
          </Link>
        </div>

        <div className="grid grid-cols-2 2xl:grid-cols-3 mt-[64px] gap-[16px]">
          {getGridsData?.map((grid) => (
            <div
              onClick={() => {
                navigate(`/grid-builder/${grid.id}`);
              }}
              key={grid.id}
              className="bg-[white] rounded-[12px] p-[24px] flex flex-col gap-[16px] cursor-pointer"
              style={{
                border: "1px solid #F4F4F4",
              }}
            >
              <div className="flex justify-between">
                <Text fontSize={20} fontWeight={400}>
                  {grid.name}
                </Text>
                <Popover>
                  <PopoverTrigger
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                  >
                    <Ellipsis color="#414651" height={24} width={24} />
                  </PopoverTrigger>
                  <PopoverContent>
                    <div className="flex flex-col gap-[16px]">
                      <Button
                        variant="secondaryOutlined"
                        className="justify-start"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/grid-builder/edit/${grid.id}`);
                        }}
                      >
                        <Pencil
                          strokeWidth="2px"
                          width={24}
                          height={24}
                          color="#414651"
                        />
                        <Text fontSize={16} fontWeight={400}>
                          {t("edit")}
                        </Text>
                      </Button>
                      <Button
                        variant="secondaryOutlined"
                        className="justify-start"
                        disabled={duplicateGridIsLoading}
                        onClick={(e) => {
                          e.stopPropagation();
                          duplicateGrid({ grid });
                        }}
                      >
                        <Copy
                          strokeWidth="2px"
                          width={24}
                          height={24}
                          color="#414651"
                        />
                        <Text fontSize={16} fontWeight={400}>
                          {t("duplicate")}
                        </Text>
                      </Button>
                      <Button
                        variant="secondaryOutlined"
                        className="justify-start"
                        onClick={(e) => {
                          e.stopPropagation();
                          setGridToUpdate(grid);
                          setIsDialogUpdatePlanificationOpen(true);
                        }}
                      >
                        <Calendar
                          strokeWidth="2px"
                          width={24}
                          height={24}
                          color="#414651"
                        />
                        <Text fontSize={16} fontWeight={400}>
                          {t("modify_schedule")}
                        </Text>
                      </Button>
                      <Button
                        variant="secondaryOutlined"
                        className="justify-start"
                        onClick={(e) => {
                          e.stopPropagation();
                          setGridToDelete(grid.id);
                          setIsDialogDeleteGridOpen(true);
                        }}
                        disabled={deleteGridIsLoading}
                      >
                        <Trash
                          strokeWidth="2px"
                          width={24}
                          height={24}
                          color="#DA1E28"
                        />
                        <Text fontSize={16} fontWeight={400} color="#DA1E28">
                          {t("delete")}
                        </Text>
                      </Button>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
              <Badge>
                <Text fontWeight={600} fontSize={14}>
                  {t("count_addresses", { count: grid.restaurantCount })}
                </Text>
              </Badge>
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export default GridBuilder;
